import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { toast } from 'sonner';
import { useLanguage } from '../i18n/LanguageContext';
import { 
  ShieldCheck, 
  Clock, 
  Users, 
  Star, 
  ChevronRight,
  MapPin,
  Calendar,
  Send,
  Phone,
  Mail,
  Award,
  Leaf,
  Waves
} from 'lucide-react';
import ReviewsWidget from '../components/ReviewsWidget';
import { localizeTour } from '../lib/tourI18n';

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [raftingTours, setRaftingTours] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursData, reviewsData, raftingData] = await Promise.all([
          api.getTours(),
          api.getReviews(),
          api.getTours('rafting')
        ]);
        setTours(toursData.slice(0, 3));
        setReviews(reviewsData);
        setRaftingTours(raftingData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.sendContactMessage(contactForm);
      toast.success('Message sent successfully! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1634771451674-349e17a8da67?auto=format&fit=crop&w=1920&q=80"
            alt="Los Sueños Marina Costa Rica"
            className="w-full h-full object-cover"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl text-center text-white">
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4 inline mr-2" />
              20+ Years of Trusted Service
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Los Sueños
            <br />
            <span className="text-[#F08A5D]">{t('hero.title2')}</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            <Link
              to="/tours"
              data-testid="hero-book-now"
              className="h-14 px-8 bg-[#F08A5D] hover:bg-[#E0794C] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg hover:shadow-xl"
            >
              {t('hero.cta')}
              <ChevronRight className="w-5 h-5" />
            </Link>
            <a
              href="#tours"
              data-testid="hero-explore"
              className="h-14 px-8 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all border border-white/20"
            >
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, text: language === 'es' ? '20+ Años de Experiencia' : '20+ Years Experience' },
              { icon: Users, text: language === 'es' ? '5000+ Viajeros Felices' : '5000+ Happy Travelers' },
              { icon: Star, text: language === 'es' ? '5 Estrellas TripAdvisor' : '5-Star TripAdvisor' },
              { icon: Clock, text: language === 'es' ? 'Soporte 24/7' : '24/7 Support' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-center gap-3 py-4">
                <item.icon className="w-6 h-6 text-[#1D4E41]" />
                <span className="text-sm font-medium text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eco-Friendly Banner */}
      <section className="py-6 bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 border-b border-emerald-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Link
            to="/environmental-policy"
            data-testid="eco-friendly-banner"
            className="group flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-center sm:text-left hover:scale-[1.01] transition-transform"
          >
            <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-emerald-500 to-[#1D4E41] rounded-full shadow-md flex-shrink-0">
              <Leaf className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 max-w-2xl">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                <span className="text-xs font-semibold tracking-wider uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  {language === 'es' ? '🌿 Operador Eco-Responsable' : '🌿 Eco-Friendly Operator'}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  {language === 'es' ? 'SINAC · ICT · Sostenibilidad' : 'SINAC · ICT · Sustainability'}
                </span>
              </div>
              <p className="text-sm md:text-base text-[#1D4E41] font-medium">
                {language === 'es'
                  ? 'Comprometidos con proteger la biodiversidad de Costa Rica. Conoce nuestra política ambiental.'
                  : 'Committed to protecting Costa Rica\'s biodiversity. Read our environmental policy.'}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1D4E41] group-hover:text-emerald-700 transition-colors whitespace-nowrap">
              {language === 'es' ? 'Ver política' : 'View policy'}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>
      </section>

      {/* Featured Tours Section */}
      <section id="tours" className="py-16 md:py-24" data-testid="tours-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <span className="text-[#F08A5D] font-medium text-sm tracking-wide uppercase">{language === 'es' ? 'Nuestros Servicios' : 'Our Services'}</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1A1A]">
              {language === 'es' ? 'Tours y Traslados' : 'Tours & Transfers'}
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {language === 'es' 
                ? 'Desde cómodos traslados al aeropuerto hasta emocionantes tours de aventura, tenemos todo lo que necesitas para una experiencia inolvidable en Costa Rica.'
                : 'From comfortable airport transfers to exciting adventure tours, we have everything you need for an unforgettable Costa Rican experience.'}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-56 bg-gray-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.map((tour, idx) => (
                <div
                  key={tour.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/tours/${tour.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/tours/${tour.id}`); } }}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-gray-100 hover:border-[#1D4E41]/20 transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:-translate-y-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1D4E41] focus:ring-offset-2"
                  data-testid={`tour-card-${idx}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={tour.image_url}
                      alt={tour.title}
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-[#1D4E41] capitalize">
                        {tour.category}
                      </span>
                    </div>
                    {tour.discount_percent ? (
                      <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded-full text-xs font-bold shadow-md" data-testid={`green-season-badge-home-${idx}`}>
                        🌿 -{tour.discount_percent}%
                      </div>
                    ) : null}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#1D4E41] transition-colors">
                      {tour.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {tour.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {tour.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {t('tours.upTo')} 5 {t('tours.pax')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        {tour.discount_percent ? (
                          <>
                            <span className="text-base text-gray-400 line-through mr-1">${tour.price}</span>
                            <span className="text-2xl font-bold text-[#1D4E41]">${Math.round(tour.price * (1 - tour.discount_percent / 100) * 100) / 100}</span>
                          </>
                        ) : (
                          <span className="text-2xl font-bold text-[#1D4E41]">${tour.price}</span>
                        )}
                        <span className="text-gray-500 text-sm"> / {t('tours.upTo')} 5 {t('tours.pax')}</span>
                      </div>
                      <Link
                        to={`/booking/${tour.id}`}
                        data-testid={`book-tour-${idx}`}
                        onClick={(e) => e.stopPropagation()}
                        className="h-10 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center gap-1 transition-all active:scale-95"
                      >
                        {t('tours.bookNow')}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/tours"
              data-testid="view-all-tours"
              className="inline-flex items-center gap-2 h-12 px-8 border-2 border-[#1D4E41] text-[#1D4E41] hover:bg-[#1D4E41] hover:text-white rounded-full font-medium transition-all"
            >
              {t('tours.viewAll')}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Rafting Section */}
      {raftingTours.length > 0 && (
        <section id="rafting" className="py-16 md:py-24 bg-[#0E2E27] relative overflow-hidden" data-testid="rafting-section">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #7BDFC4 0, transparent 40%), radial-gradient(circle at 80% 70%, #4FB3A0 0, transparent 40%)' }}></div>
          <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 text-[#7BDFC4] font-medium text-sm tracking-wide uppercase">
                <Waves className="w-4 h-4" /> {language === 'es' ? 'Aventura en el Río' : 'River Adventure'}
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-white">
                {language === 'es' ? 'Rafting en Aguas Bravas' : 'White Water Rafting'}
              </h2>
              <p className="text-emerald-100/80 mt-4 max-w-2xl mx-auto leading-relaxed">
                {language === 'es'
                  ? 'La aventura más segura y emocionante para disfrutar en familia. Guías bilingües certificados, equipo completo, frutas frescas y almuerzo. ¡Elige tu río!'
                  : 'The safest and most thrilling adventure to enjoy with the family. Bilingual certified guides, full gear, fresh fruit and lunch. Pick your river!'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {raftingTours.map((tour, idx) => {
                const badgeMap = {
                  'tour-rafting-balsa': language === 'es' ? 'Mañana · Clase II-III' : 'Morning · Class II-III',
                  'tour-rafting-sarapiqui': language === 'es' ? 'Tarde · Clase III' : 'Afternoon · Class III',
                  'tour-rafting-sarapiqui-extreme': language === 'es' ? 'Extremo · Clase III-IV' : 'Extreme · Class III-IV',
                };
                const badge = badgeMap[tour.id] || (language === 'es' ? 'Aventura' : 'Adventure');
                const isExtreme = tour.id === 'tour-rafting-sarapiqui-extreme';
                const minAge = isExtreme ? 14 : 6;
                const tl = localizeTour(tour, language);
                return (
                <div
                  key={tour.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/tours/${tour.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/tours/${tour.id}`); } }}
                  data-testid={`rafting-card-${idx}`}
                  className="group rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#7BDFC4]/40 transition-all duration-300 hover:-translate-y-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#7BDFC4] focus:ring-offset-2"
                >
                  <div className="relative h-60 overflow-hidden">
                    <img src={tour.image_url} alt={tl.title} decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E2E27] via-transparent to-transparent"></div>
                    <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold ${isExtreme ? 'bg-[#F08A5D] text-white' : 'bg-[#7BDFC4] text-[#0E2E27]'}`}>
                      {badge}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#7BDFC4] transition-colors">{tl.title}</h3>
                    <p className="text-emerald-100/70 text-sm mb-4 line-clamp-3">{tl.description}</p>
                    <div className="flex items-center gap-4 text-sm text-emerald-100/80 mb-5">
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {tl.duration}</span>
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {language === 'es' ? `Edad mín. ${minAge}` : `Min age ${minAge}`}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-white">${tour.price}</span>
                        <span className="text-emerald-100/70 text-sm"> / {language === 'es' ? 'persona' : 'person'}</span>
                      </div>
                      <Link
                        to={`/booking/${tour.id}`}
                        data-testid={`book-rafting-${idx}`}
                        onClick={(e) => e.stopPropagation()}
                        className="h-10 px-5 bg-[#7BDFC4] hover:bg-white text-[#0E2E27] rounded-full font-semibold flex items-center gap-1 transition-all active:scale-95"
                      >
                        {t('tours.bookNow')}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            <p className="text-center text-emerald-100/70 text-sm mt-8 max-w-3xl mx-auto" data-testid="rafting-transport-note-home">
              <Waves className="w-4 h-4 inline-block mr-1 -mt-0.5" />
              {language === 'es'
                ? 'El transporte solo está incluido dentro del área del Volcán Arenal. Si requieres transporte desde otra zona, comunícate con Travel Guide Costa Rica para cotizar y agendar el pickup.'
                : 'Transport is only included within the Arenal Volcano area. If you need transport from another area, please contact Travel Guide Costa Rica to quote and schedule your pickup.'}
            </p>
          </div>
        </section>
      )}

      <section id="about" className="py-16 md:py-24 bg-white" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1770848891773-9d866a56dd3d?crop=entropy&cs=srgb&fm=jpg&w=900&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxDb3N0YSUyMFJpY2ElMjB0cm9waWNhbCUyMGJlYWNoJTIwdHVycXVvaXNlJTIwd2F0ZXJ8ZW58MHx8fHwxNzcyOTA0NjcwfDA&ixlib=rb-4.1.0&q=80"
                alt="Costa Rica Beach"
                loading="lazy"
                decoding="async"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1D4E41] text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">20+</div>
                <div className="text-sm text-white/80">{t('about.experience')}</div>
              </div>
            </div>
            <div>
              <span className="text-[#F08A5D] font-medium text-sm tracking-wide uppercase">{language === 'es' ? 'Sobre Nosotros' : 'About Us'}</span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1A1A]">
                {language === 'es' ? 'Tu Socio de Viaje de Confianza' : 'Your Trusted Travel Partner'}
              </h2>
              <p className="text-gray-600 mt-6 leading-relaxed">
                {language === 'es' 
                  ? 'En Travel Guide Costa Rica, nuestro equipo experimentado de conductores y guías turísticos, con más de 20 años de experiencia, está comprometido a crear unas vacaciones inolvidables en Costa Rica para ti.'
                  : 'At Travel Guide Costa Rica, our seasoned team of experienced drivers and tour guides, with over 20 years of expertise, is committed to crafting an unforgettable Costa Rican vacation for you.'}
              </p>
              <p className="text-gray-600 mt-4 leading-relaxed">
                {language === 'es'
                  ? 'Somos más que una empresa; somos un colectivo de personas apasionadas dedicadas a brindar un servicio excepcional y crear recuerdos preciados para nuestros valiosos clientes.'
                  : 'We are more than just a company; we are a collective of passionate individuals dedicated to delivering exceptional service and creating cherished memories for our valued clients.'}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mt-8">
                {[
                  { icon: Award, title: language === 'es' ? 'Licenciado y Asegurado' : 'Licensed & Insured', desc: language === 'es' ? 'Conductores certificados' : 'Fully certified drivers' },
                  { icon: ShieldCheck, title: language === 'es' ? 'Seguro y Confiable' : 'Safe & Reliable', desc: language === 'es' ? 'Tu seguridad es nuestra prioridad' : 'Your safety is our priority' },
                  { icon: MapPin, title: language === 'es' ? 'Expertos Locales' : 'Local Experts', desc: language === 'es' ? 'Conocimiento profundo de Costa Rica' : 'Deep knowledge of Costa Rica' },
                  { icon: Clock, title: language === 'es' ? 'Servicio Puntual' : 'Punctual Service', desc: language === 'es' ? 'Siempre a tiempo' : 'Always on time' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#E8F5E9] rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#1D4E41]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1A1A1A]">{item.title}</div>
                      <div className="text-sm text-gray-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-16 md:py-24 bg-[#FAFAF8]" data-testid="reviews-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <span className="text-[#F08A5D] font-medium text-sm tracking-wide uppercase">{language === 'es' ? 'Testimonios' : 'Testimonials'}</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1A1A]">
              {t('reviews.title')}
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {language === 'es'
                ? 'No solo confíes en nuestra palabra. Esto es lo que dicen los viajeros de todo el mundo sobre su experiencia con nosotros.'
                : "Don't just take our word for it. Here's what travelers from around the world have to say about their experience with us."}
            </p>
          </div>

          <ReviewsWidget reviews={reviews} lang={language} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-white" data-testid="contact-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="text-[#F08A5D] font-medium text-sm tracking-wide uppercase">{t('contact.title')}</span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1A1A]">
                {language === 'es' ? 'Planifiquemos Tu Viaje' : "Let's Plan Your Trip"}
              </h2>
              <p className="text-gray-600 mt-6 leading-relaxed">
                {language === 'es'
                  ? '¿Tienes preguntas sobre nuestros servicios? ¿Necesitas ayuda para planificar tu aventura en Costa Rica? ¡Estamos aquí para ayudarte!'
                  : "Have questions about our services? Need help planning your Costa Rican adventure? We're here to help! Reach out to us and our team will get back to you promptly."}
              </p>

              <div className="mt-8 space-y-6">
                <a href="tel:+50688173167" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center group-hover:bg-[#1D4E41] transition-colors">
                    <Phone className="w-5 h-5 text-[#1D4E41] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A1A1A]">{language === 'es' ? 'Llámanos' : 'Call Us'}</div>
                    <div className="text-gray-500">+506 8817-3167</div>
                  </div>
                </a>
                <a href="mailto:reservations@travelguide.cr" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center group-hover:bg-[#1D4E41] transition-colors">
                    <Mail className="w-5 h-5 text-[#1D4E41] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A1A1A]">{language === 'es' ? 'Escríbenos' : 'Email Us'}</div>
                    <div className="text-gray-500">reservations@travelguide.cr</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#1D4E41]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A1A1A]">{t('contact.location')}</div>
                    <div className="text-gray-500">San Pedro de Poas, Costa Rica</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100">
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.name')}</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.yourEmail')}</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    data-testid="contact-phone"
                    className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] bg-white outline-none transition-all resize-none"
                    placeholder="Tell us about your travel plans..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="w-full h-12 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
